/**
 * Caption settings for one block. Each field overrides the site caption setting
 * of the same name for the captions inside that block, including the viewer
 * title once one of its images or videos is opened.
 *
 * Stored on the block as `caption: { faceId, size, lineHeight, tracking, weight, style, casing, color }`.
 * Missing fields inherit.
 */

import { resolveFace } from '../library/resolve.js';
import { cssLength } from '../theme/header-modules.js';
import { BLOCK_TYPES } from './schema.js';
import { normalizeHex } from '../theme/colors.js';

export const CAPTION_LIMITS = {
    size: { min: 0.4, max: 3, step: 0.025 },
    lineHeight: { min: 0.8, max: 2.4, step: 0.05 },
    tracking: { min: 0, max: 0.6, step: 0.01 },
    weight: { min: 100, max: 900, step: 100 },
};

export const CAPTION_CASINGS = ['uppercase', 'lowercase', 'capitalize', 'none'];

function clampNumber(value, { min, max }) {
    const number = Number(value);
    if (value === '' || value == null || !Number.isFinite(number)) return null;
    return Math.min(max, Math.max(min, number));
}

/**
 * @param {Object} raw
 * @returns {Object|null} Only the valid fields, or null when nothing is set
 */
export function cleanCaption(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const next = {};
    if (typeof raw.faceId === 'string' && raw.faceId.trim()) next.faceId = raw.faceId.trim();
    ['size', 'lineHeight', 'tracking', 'weight'].forEach((key) => {
        const value = clampNumber(raw[key], CAPTION_LIMITS[key]);
        if (value != null) next[key] = value;
    });
    if (raw.style === 'italic' || raw.style === 'normal') next.style = raw.style;
    if (CAPTION_CASINGS.includes(raw.casing)) next.casing = raw.casing;
    const color = normalizeHex(raw.color);
    if (color) next.color = color;
    return Object.keys(next).length ? next : null;
}

export function hasBlockCaption(block) {
    return Boolean(cleanCaption(block?.caption));
}

/**
 * CSS custom properties that scope the caption settings to one element.
 * @param {Object} caption
 * @param {Object} [library]
 * @returns {{ vars: Object, face: Object|null }}
 */
export function captionVars(caption, library) {
    const clean = cleanCaption(caption);
    if (!clean) return { vars: {}, face: null };
    const vars = {};
    let face = null;
    if (clean.faceId) {
        const resolved = resolveFace(clean.faceId, library);
        vars['--font-caption'] = resolved.css;
        vars['--caption-squash'] = 'none';
        face = resolved.face || null;
    }
    if (clean.size != null) vars['--caption-size'] = cssLength(clean.size, 'rem');
    if (clean.lineHeight != null) vars['--caption-leading'] = String(clean.lineHeight);
    if (clean.tracking != null) vars['--caption-tracking'] = cssLength(clean.tracking, 'em');
    if (clean.weight != null) vars['--caption-weight'] = String(clean.weight);
    if (clean.style) vars['--caption-style'] = clean.style;
    if (clean.casing) vars['--caption-casing'] = clean.casing;
    if (clean.color) vars['--caption-color'] = clean.color;
    return { vars, face };
}

function itemHasFile(item, fileId) {
    if (!item) return false;
    if (item.fileId === fileId || item.id === fileId) return true;
    return Array.isArray(item.sides) && item.sides.some((side) => side?.fileId === fileId);
}

/**
 * The block on a page that shows a file, searching column cells too.
 * @param {Array} blocks
 * @param {string} fileId
 * @returns {Object|null}
 */
export function blockForMedia(blocks, fileId) {
    if (!fileId || !Array.isArray(blocks)) return null;
    for (const block of blocks) {
        if (block?.type === BLOCK_TYPES.GALLERY && (block.items || []).some((item) => itemHasFile(item, fileId))) {
            return block;
        }
        if (block?.type === BLOCK_TYPES.COLUMNS) {
            const found = blockForMedia(block.cells, fileId);
            if (found) return found;
        }
    }
    return null;
}
